<template>
  <div
    v-outside-click="close"
    :class="b()"
  >
    <button
      :class="b('toggle')"
      type="button"
      @click="onToggleClick"
    >
      {{ toggleLabel }}
    </button>

    <!-- Modifier syntax did not work for name attribute. -->
    <transition :name="b('menu-wrapper--fade')">
      <div
        v-if="showMenu"
        :class="b('menu-wrapper')"
      >
        <ul :class="b('menu')">
          <li :class="b('menu-item', { title: true })">
            {{ $t('c-language-menu.title') }}
          </li>
          <li
            v-for="language in sessionStore.languages"
            :key="language.iso"
            :class="b('menu-item', { active: language.isSelected })"
          >
            <a
              :class="b('menu-link')"
              :href="language.url"
            >
              {{ language.name }}
            </a>
          </li>
          <li :class="b('menu-item')">
            <button
              :class="b('country-toggle')"
              type="button"
              @click="sessionStore.showMarketSelector = true"
            >
              {{ $t('c-language-menu.changeRegion') }}
            </button>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
  import useSessionStore from '@/stores/session';

  /**
   * Renders a menu to change the pge language.
   */
  export default {
    name: 'c-language-menu',

    // components: {},
    // mixins: [],

    // props: {},
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Defines the status of the menu.
         */
        showMenu: false,
      };
    },

    computed: {
      /**
       * Extracts the toggle label from the language ISO.
       *
       * @returns {String}
       */
      toggleLabel() {
        const [language, country] = this.$i18n.locale.split('-');

        return language + (country ? ` (${country})` : '');
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Click handler for the menu toggle.
       */
      onToggleClick() {
        if (!this.sessionStore.markets.length) {
          this.sessionStore.apiFetchMarkets();
        }

        if (this.sessionStore.languages.length > 0) {
          this.showMenu = !this.showMenu;
        } else {
          this.sessionStore.showMarketSelector = true;
        }
      },

      /**
       * Closes the menu.
       */
      close() {
        this.showMenu = false;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-language-menu {
    $pointer-indent: variables.$spacing--30;
    $pointer-size: 20px;

    position: relative;

    &__toggle {
      @extend %button-reset;
      @include mixins.font(variables.$font-size--20);

      border-bottom: functions.rem(1px) solid variables.$color-primary--1;
      color: variables.$color-primary--1;
      font-weight: variables.$font-weight--light;
      text-transform: uppercase;
    }

    &__menu-wrapper {
      @include mixins.font(variables.$font-size--16);
      @include mixins.z-index(languageMenu);

      position: absolute;
      top: calc(100% + #{functions.rem($pointer-size)});
      right: calc(50% - #{functions.rem($pointer-indent + math.div($pointer-size, 2))});
      background: variables.$color-grayscale--1000;
      color: variables.$color-primary--1;
      white-space: nowrap;

      &::before {
        @include mixins.z-index(back); // Places the menu shadow behind the pointer.

        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        content: '';
        box-shadow: 0 0 6px rgba(variables.$color-grayscale--0, 0.2);
      }

      &::after {
        @include mixins.z-index(back); // Places the pointer behind the menu content.

        position: absolute;
        top: 0;
        right: functions.rem($pointer-indent);
        display: block;
        content: '';
        width: functions.rem($pointer-size);
        height: functions.rem($pointer-size);
        background: variables.$color-grayscale--1000;
        box-shadow: 0 0 6px rgba(variables.$color-grayscale--0, 0.2);
        transform: translateY(-50%) rotate(45deg);
      }

      &--fade-enter-active,
      &--fade-leave-active {
        transition: opacity variables.$transition-duration--200 ease-out;
      }

      &--fade-enter-from,
      &--fade-leave-to {
        opacity: 0;
      }
    }

    &__menu {
      @extend %list-reset;
      @include mixins.z-index(front); // Moves the menu content before the pointer.

      min-width: functions.rem(235px);
      background: variables.$color-grayscale--1000;
    }

    &__menu-item {
      font-weight: variables.$font-weight--light;
      position: relative;

      &:not(:last-child) {
        border-bottom: 1px solid variables.$color-grayscale--900;
      }

      &--title {
        @include mixins.font(variables.$font-size--10);

        color: variables.$color-grayscale--100;
        font-weight: variables.$font-weight--semi-bold;
      }

      &--active {
        font-weight: variables.$font-weight--semi-bold;
      }

      &--active::after {
        @include mixins.iconSprite('i-check', $color: variables.$color-grayscale--100);

        content: '';
        position: absolute;
        right: 1rem;
        top: 1rem;
      }
    }

    &__menu-link,
    &__menu-item--title,
    &__country-toggle {
      display: flex;
      align-items: center;
      height: functions.rem(45px);
      min-height: functions.rem(45px);
      padding: functions.rem(0 variables.$spacing--40 0 variables.$spacing--20);
    }

    &__menu-link {
      color: variables.$color-primary--1;
    }

    &__menu-item:not(&__menu-item--active) &__menu-link:hover {
      color: variables.$color-primary--2;
    }

    &__country-toggle {
      @extend %button-reset;

      min-width: 100%;
      background: variables.$color-primary--1;
      color: variables.$color-grayscale--1000;
      transition: background-color variables.$transition-duration--300 ease-out;
      position: relative;

      &:hover {
        background-color: variables.$color-secondary--7;
      }

      &::after {
        @include mixins.iconSprite('i-globe', $color: variables.$color-grayscale--1000);

        content: '';
        right: 1rem;
        position: absolute;
      }
    }
  }
</style>
